import { useState, useEffect, useCallback } from "react";

/** Matches API `ModelSpeed` values for `/api/Report/ProcessWithAI`. */
export const MODEL_SPEED_SMARTER = "Smarter";
export const MODEL_SPEED_FASTEST = "Fastest";

const COOKIE_NAME = "medilis_process_ai_model_speed";
const MAX_AGE_SEC = 365 * 24 * 60 * 60;

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${escaped}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1].trim()) : null;
}

function writeCookie(name, value, maxAgeSec) {
  if (typeof document === "undefined") return;
  const encoded = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  document.cookie = `${encoded};path=/;max-age=${maxAgeSec};SameSite=Lax`;
}

function normalizeModelSpeed(value) {
  return value === MODEL_SPEED_FASTEST ? MODEL_SPEED_FASTEST : MODEL_SPEED_SMARTER;
}

/**
 * Persists the user's ProcessWithAI model speed preference in a cookie.
 * @returns {[string, (value: string) => void]}
 */
export function useProcessAiModelSpeed() {
  const [modelSpeed, setModelSpeedState] = useState(() => {
    const stored = readCookie(COOKIE_NAME);
    if (stored === MODEL_SPEED_FASTEST || stored === MODEL_SPEED_SMARTER) {
      return stored;
    }
    return MODEL_SPEED_SMARTER;
  });

  const setModelSpeed = useCallback((value) => {
    setModelSpeedState(normalizeModelSpeed(value));
  }, []);

  useEffect(() => {
    try {
      writeCookie(COOKIE_NAME, modelSpeed, MAX_AGE_SEC);
    } catch (e) {
      console.warn("Failed to persist model speed cookie:", e);
    }
  }, [modelSpeed]);

  return [modelSpeed, setModelSpeed];
}
